import { Copy, ExternalLink, Eye, EyeOff, RefreshCw } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { PROJECT_STATUSES } from "./OrgDetailPage";
import { PrototypeSharesCard } from "./PrototypeSharesCard";
import { useAdminOrg, useAdminProject, usePatchProject, useRotateToken } from "@/api/hooks/admin";
import { errorMessage } from "@/api/errors";
import type { ProjectStatus } from "@/api/types";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { ErrorNotice } from "@/components/ErrorNotice";
import { Field, FormError } from "@/components/Field";
import { PageHeader } from "@/components/PageHeader";
import { PageLoading } from "@/components/Spinner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { widgetSnippet } from "@/lib/widgetSnippet";
import { usePageCrumbs } from "@/app/layouts/shellContext";
import { Banner } from "@/components/ui/banner";

type Values = {
  name: string;
  description: string;
  goals: string;
  status: ProjectStatus;
  primary_contact_user_id: string;
};

function mask(token: string): string {
  return `${token.slice(0, 4)}${"•".repeat(Math.max(8, token.length - 8))}${token.slice(-4)}`;
}

export function ProjectAdminPage() {
  const { t } = useTranslation();
  const { id = "" } = useParams();
  const project = useAdminProject(id);
  const org = useAdminOrg(project.data?.organisation_id);
  const patch = usePatchProject(id);
  const rotate = useRotateToken(id);
  const [revealed, setRevealed] = useState(false);
  const [confirmRotate, setConfirmRotate] = useState(false);
  const form = useForm<Values>();
  usePageCrumbs([
    { label: t("admin.nav.orgs"), to: "/admin" },
    ...(org.data ? [{ label: org.data.name, to: `/admin/orgs/${org.data.id}` }] : []),
    { label: project.data?.name ?? "" },
  ]);

  useEffect(() => {
    const p = project.data;
    if (!p) return;
    form.reset({
      name: p.name,
      description: p.description,
      goals: p.goals,
      status: p.status,
      primary_contact_user_id: p.primary_contact_user_id ?? "",
    });
    // Reset only when a different project loads, not on every refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.data?.id]);

  if (project.isPending) return <PageLoading />;
  if (project.isError) return <ErrorNotice error={project.error} />;
  const p = project.data;
  const snippet = widgetSnippet(p.feedback_token);

  const onSubmit = form.handleSubmit((v) =>
    patch.mutate(
      { ...v, primary_contact_user_id: v.primary_contact_user_id || null },
      { onSuccess: () => toast.success(t("admin.project.saved")) },
    ),
  );

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={p.name}
        description={org.data ? <Link to={`/admin/orgs/${org.data.id}`}>{org.data.name}</Link> : null}
        actions={
          <Button variant="secondary" asChild>
            <Link to={`/${p.organisation_slug}/${p.slug}`}>
              <ExternalLink aria-hidden="true" className="h-4 w-4" />
              {t("admin.project.viewAsClient")}
            </Link>
          </Button>
        }
      />

      {p.status === "requested" ? (
        <Banner
          tone="warning"
          title={t("admin.project.requestTitle")}
          action={
            <Button
              variant="solid"
              size="sm"
              disabled={patch.isPending}
              onClick={() =>
                patch.mutate(
                  { status: "discovery" },
                  {
                    onSuccess: (np) => {
                      form.setValue("status", np.status);
                      toast.success(t("admin.project.movedToDiscovery"));
                    },
                  },
                )
              }
            >
              {t("admin.project.moveToDiscovery")}
            </Button>
          }
        >
          {t("admin.project.requestBody", {
            type: p.project_type
              ? p.project_type === "other" && p.project_type_other
                ? p.project_type_other
                : t(`project.type.${p.project_type}`)
              : t("project.type.other"),
          })}
        </Banner>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-split">
        <Card>
          <CardHeader>
            <h2 className="text-xl">{t("admin.project.details")}</h2>
          </CardHeader>
          <CardContent>
            <form noValidate onSubmit={onSubmit} className="flex flex-col gap-4">
              <FormError message={patch.isError ? errorMessage(patch.error, t("errors.generic")) : null} />
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label={t("admin.project.name")}>{(f) => <Input {...f} {...form.register("name")} />}</Field>
                <Field label={t("admin.project.slug")} hint={t("admin.project.slugHint")}>
                  {(f) => <Input {...f} value={p.slug} readOnly />}
                </Field>
              </div>
              <Field label={t("admin.project.description")} hint={t("admin.project.descriptionHint")}>
                {(f) => <Textarea {...f} rows={5} {...form.register("description")} />}
              </Field>
              <Field label={t("admin.project.goals")}>{(f) => <Textarea {...f} rows={3} {...form.register("goals")} />}</Field>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label={t("admin.project.status")}>
                  {(f) => (
                    <Select {...f} className="w-full" {...form.register("status")}>
                      {PROJECT_STATUSES.map((s) => (
                        <option key={s} value={s}>
                          {t(`project.status.${s}`)}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>
                <Field label={t("admin.project.primaryContact")}>
                  {(f) => (
                    <Select {...f} className="w-full" {...form.register("primary_contact_user_id")}>
                      <option value="">{t("admin.project.noContact")}</option>
                      {(org.data?.users ?? []).map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.name}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>
              </div>
              <div>
                <Button type="submit" disabled={patch.isPending}>
                  {t("admin.project.save")}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        <div className="flex flex-col gap-6">
          <Card>
            <CardHeader>
              <h2 className="text-xl">{t("admin.project.questionnaire")}</h2>
              <p className="text-sm text-fg-muted">
                {p.latest_version ? t("admin.project.latestVersion", { version: p.latest_version }) : t("admin.project.noVersion")}
              </p>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              <Button asChild variant="secondary">
                <Link to={`/admin/projects/${p.id}/publish`}>{t("admin.project.publish")}</Link>
              </Button>
              <Button asChild variant="ghost">
                <Link to={`/admin/projects/${p.id}/responses`}>{t("admin.project.responses")}</Link>
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <h2 className="text-xl">{t("admin.project.feedbackTitle")}</h2>
              <p className="text-sm text-fg-muted">{t("admin.project.feedbackIntro")}</p>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <span id="token-label" className="font-medium">
                  {t("admin.project.token")}
                </span>
                <div className="flex flex-wrap items-center gap-2">
                  <code aria-labelledby="token-label" className="flex-1 break-all rounded-md bg-surface-muted px-3 py-2 font-mono text-sm">
                    {revealed ? p.feedback_token : mask(p.feedback_token)}
                  </code>
                  <Button size="sm" variant="ghost" onClick={() => setRevealed((r) => !r)} aria-pressed={revealed}>
                    {revealed ? <EyeOff aria-hidden="true" className="h-4 w-4" /> : <Eye aria-hidden="true" className="h-4 w-4" />}
                    {revealed ? t("admin.project.hide") : t("admin.project.reveal")}
                  </Button>
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <span id="snippet-label" className="font-medium">
                  {t("admin.project.snippet")}
                </span>
                <pre
                  aria-labelledby="snippet-label"
                  className="relative overflow-x-auto rounded-md bg-fg p-4 font-mono text-xs leading-5 text-fg-inverse"
                >
                  {snippet}
                </pre>
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => void navigator.clipboard?.writeText(snippet).then(() => toast.success(t("admin.project.copied")))}
                  >
                    <Copy aria-hidden="true" className="h-4 w-4" />
                    {t("admin.project.copy")}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setConfirmRotate(true)}>
                    <RefreshCw aria-hidden="true" className="h-4 w-4" />
                    {t("admin.project.rotate")}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <PrototypeSharesCard projectId={p.id} users={org.data?.users ?? []} />

      <ConfirmDialog
        open={confirmRotate}
        onOpenChange={setConfirmRotate}
        title={t("admin.project.rotateConfirm")}
        description={t("admin.project.rotateBody")}
        confirmLabel={t("admin.project.rotate")}
        busy={rotate.isPending}
        onConfirm={() =>
          rotate.mutate(undefined, {
            onSuccess: () => toast.success(t("admin.project.rotated")),
            onError: (e) => toast.error(errorMessage(e, t("errors.generic"))),
            onSettled: () => setConfirmRotate(false),
          })
        }
      />
    </div>
  );
}
